import React, { useState, useEffect, useContext } from 'react';
import {
  Typography,
  Button,
  Row,
  Col,
  Card,
  Input,
  Space,
  Tag,
  Divider,
  Tooltip,
  Pagination,
  message,
  Spin
} from 'antd';
import { ThemeContext } from '../../context/ThemeContext';
import {
  SearchOutlined,
  ArrowRightOutlined,
  BankOutlined,
  CarOutlined,
  HomeOutlined,
  RocketOutlined,
  ShopOutlined,
  SafetyOutlined,
  ArrowUpOutlined,
  PlusOutlined,
  StarOutlined,
  LineChartOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  CrownOutlined,
  GlobalOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import LandingLayout from './LandingLayout';
import './Issuers.css';
import apiClient from '../../services/apiClient';

const { Title, Paragraph } = Typography;
const { Search } = Input;

const Issuers = ({ skipLayout = false }) => {
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [issuersData, setIssuersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [categoryCounts, setCategoryCounts] = useState({});

  // Background colors for issuer cards
  const backgroundColors = [
    '#F3CDCA',
    '#D9CAF2',
    '#F2C5E5',
    '#CBF2F3',
    '#CBF3D9'
  ];

  // Base URL from environment variable
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

  // Issuer category definitions
  const issuerCategories = {
    ALL: "All Categories",
    promoter_owned_companies: "Promoter Owned Companies",
    government_owned_companies: "Government Owned Companies", 
    institutionally_owned_companies: "Institutionally Owned Companies",
    widely_held_companies: "Widely Held Companies",
    foreign_owned_companies: "Foreign Owned Companies",
    non_promoter_owned_companies: "Non-Promoter Owned Companies",
    non_government_owned_companies: "Non-Government Owned Companies",
    non_institutionally_owned_companies: "Non-Institutionally Owned Companies",
    non_foreign_owned_companies: "Non-Foreign Owned Companies"
  };

  // Category array for buttons
  const categoryButtons = [
    { key: 'ALL', label: 'All Categories', icon: <PlusOutlined /> },
    { key: 'promoter_owned_companies', label: 'Promoter Owned', icon: <CrownOutlined /> },
    { key: 'government_owned_companies', label: 'Government Owned', icon: <HomeOutlined /> },
    { key: 'institutionally_owned_companies', label: 'Institutional', icon: <BankOutlined /> },
    { key: 'widely_held_companies', label: 'Widely Held', icon: <TeamOutlined /> },
    { key: 'foreign_owned_companies', label: 'Foreign Owned', icon: <GlobalOutlined /> },
    { key: 'non_promoter_owned_companies', label: 'Non-Promoter Owned', icon: <StarOutlined /> },
    { key: 'non_government_owned_companies', label: 'Non-Government Owned', icon: <SafetyOutlined /> },
    { key: 'non_institutionally_owned_companies', label: 'Non-Institutional', icon: <ShopOutlined /> },
    { key: 'non_foreign_owned_companies', label: 'Non-Foreign Owned', icon: <CarOutlined /> }
  ];

  // Fetch issuers data with pagination and category filtering
  const fetchIssuersData = async (page = currentPage, size = pageSize, category = selectedCategory) => {
    try {
      setLoading(true);
      let url;
      
      if (category === 'ALL') {
        url = `${baseUrl}/master-issuer/all?page=${page - 1}&size=${size}`;
      } else {
        url = `${baseUrl}/isin-category/issuers/category/${category}?page=${page - 1}&size=${size}`;
      }
      
      const response = await apiClient.get(url);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = response.data;

      // Handle the new API response structure
      if (result.statusCode === 200 && result.data && result.data.content && Array.isArray(result.data.content)) {
        const data = result.data.content;
        setIssuersData(data);
        setTotalItems(result.data.totalElements || data.length);
        setCurrentPage(result.data.pageNumber + 1);
        setPageSize(result.data.pageSize || size);
        console.log('Issuers API Response:', result);
      } else if (Array.isArray(result)) {
        // Fallback for old structure
        const data = result;
        setIssuersData(data);
        setTotalItems(data.length);
        console.log('Issuers API Response (fallback):', result);
      } else {
        console.error('Invalid issuers API response structure:', result);
        setIssuersData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching issuers data:', error);
      message.error('Failed to fetch issuers data');
      setIssuersData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch category statistics
  const fetchCategoryStatistics = async () => {
    try {
      const response = await apiClient.get(`${baseUrl}/isin-category/issuers/statistics`);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = response.data;
      
      if (result.statusCode === 200 && result.data) {
        setCategoryCounts(result.data);
        console.log('Category statistics:', result.data);
      }
    } catch (error) {
      console.error('Error fetching category statistics:', error);
    }
  };

  // Handle page change
  const handlePageChange = (page, size) => {
    fetchIssuersData(page, size, selectedCategory);
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    fetchIssuersData(1, pageSize, category);
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
    // Reset to first page when searching
    fetchIssuersData(1, pageSize, selectedCategory);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchIssuersData(1, pageSize);
    fetchCategoryStatistics();
  }, []);

  // Helper function to get issuer logo/icon
  const getIssuerLogo = (issuer, industry) => {
    const name = issuer.split(' ')[0].toUpperCase();

    // Industry-based icons
    if (industry === 'Banks') return '🏦';
    if (industry === 'Insurance') return '🛡️';
    if (industry === 'Finance') return '💼';
    if (industry === 'Auto Components') return '🚗';

    // Default to first letter
    return name.charAt(0);
  };

  // Helper function to get issuer short name
  const getIssuerShortName = (issuer) => {
    return issuer;
  };

  // Helper function to get truncated text for stats
  const getTruncatedText = (text, maxLength = 20) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleViewDetails = (issuer) => {
    // Use corporateIdentityNumber as issuerId
    const issuerId = issuer.corporateIdentityNumber;
    // Navigate to dashboard route if we're in dashboard context, otherwise to landing page
    const isInDashboard = window.location.pathname.includes('/d/');
    if (isInDashboard) {
      navigate(`/d/issuer/details?issuerId=${issuerId}`);
    } else {
      navigate(`/issuer-details/${issuerId}`);
    }
  };

  // Helper function to get category color
  const getCategoryColor = (industry) => {
    const colors = {
      'Banks': 'blue',
      'Insurance': 'green',
      'Finance': 'purple',
      'Auto Components': 'orange'
    };
    return colors[industry] || 'default';
  };

  // Helper function to get category icon
  const getCategoryIcon = (industry) => {
    const icons = {
      'Banks': <BankOutlined />,
      'Insurance': <SafetyOutlined />,
      'Finance': <BankOutlined />,
      'Auto Components': <CarOutlined />
    };
    return icons[industry] || <ShopOutlined />;
  };

  const content = (
    <div className={`issuers-page ${isDark ? 'dark' : ''}`}>
      <div className="container">
        {/* Hero Section Card */}
        {/* <div className="hero-section-card">
          <div className="page-header">
            <div className="header-content">
              <Title level={1} className="page-title">
                Explore by Issuers
              </Title>
              <Paragraph className="page-subtitle">
                Discover bond issuers across various sectors and explore their offerings
              </Paragraph>
            </div>
            <div className="search-input-container">
              <Input
                placeholder="Search by Issuer name"
                prefix={<SearchOutlined />}
                value={searchTerm}
                size="large"
                style={{ borderRadius: 6 }}
                allowClear
              />
            </div>

          </div>
        </div> */}

        {/* Categories Section */}
        <div className="categories-section">
          <Title level={1} className="categories-title">Issuer Categories</Title>
          <div className="category-buttons">
            {categoryButtons.map((category) => (
              <Button
                key={category.key}
                type={selectedCategory === category.key ? 'primary' : 'default'}
                icon={category.icon}
                className={`category-btn ${selectedCategory === category.key ? 'active' : ''}`}
                onClick={() => handleCategoryChange(category.key)}
              >
                {category.label}
                {category.key !== 'ALL' && categoryCounts[category.key] && (
                  <span className="category-count"> ({categoryCounts[category.key]})</span>
                )}
              </Button>
            ))}
          </div>
        </div>

        {/* Issuers Grid */}
        <div className="issuers-grid">
          <Row gutter={[24, 24]}>
            {loading ? (
              // Loading state
              Array.from({ length: 8 }).map((_, index) => (
                <Col xs={24} sm={12} lg={8} key={index}>
                  <Card className="bond-card loading" hoverable style={{ borderRadius: '16px' }}>
                    <div className="bond-card-inner">
                      {/* Company Name and Category */}
                      <div className="issuer-header-section">
                        <div className="issuer-company-info">
                          <div className="company-logo">
                            <div className="company-logo-text loading-placeholder" style={{ width: '64px', height: '64px', backgroundColor: '#f0f0f0', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 'bold', color: '#999' }}>...</div>
                          </div>
                          <div className="issuer-text-info">
                            <div className="issuer-company-name">
                              <div className="loading-placeholder" style={{ width: '90%', height: '24px', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '8px' }}></div>
                            </div>
                            <div className="issuer-category">
                              <div className="loading-placeholder" style={{ width: '60%', height: '16px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}></div>
                            </div>
                          </div>
                        </div>
                        <div className="issuer-right-section">
                          <div className="loading-placeholder" style={{ width: '48px', height: '48px', backgroundColor: '#f0f0f0', borderRadius: '50%' }}></div>
                        </div>
                      </div>

                      {/* Bottom Section: Tags */}
                      <div className="issuer-bottom-section">
                        <div className="issuer-tags">
                          <div className="loading-placeholder" style={{ width: '80px', height: '24px', backgroundColor: '#f0f0f0', borderRadius: '12px', marginRight: '8px' }}></div>
                          <div className="loading-placeholder" style={{ width: '100px', height: '24px', backgroundColor: '#f0f0f0', borderRadius: '12px' }}></div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))
            ) : (
              // Real data from API
              issuersData.map((issuer, index) => {
                const backgroundColor = backgroundColors[index % backgroundColors.length];
                return (
                  <Col xs={24} sm={12} lg={8} key={index}>
                    <Card
                      className="bond-card"
                      hoverable
                      onClick={() => handleViewDetails(issuer)}
                      style={{
                        borderRadius: '16px',
                        backgroundColor: backgroundColor,
                        '--card-bg-color': backgroundColor,
                        cursor: 'pointer'
                      }}
                    >
                      <div className="bond-card-inner">
                        {/* Company Name and Category */}
                        <div className="issuer-header-section">
                          <div className="issuer-company-info">
                            <div className="company-logo">
                              <div className="company-logo-text">
                                {(() => {
                                  const issuerName = issuer.issuer || 'AXIS MAX LIFE INSURANCE LIMITED';
                                  // Extract first letter of each major word or use specific logic for known companies
                                  if (issuerName.includes('HDFC')) return 'HDFC';
                                  if (issuerName.includes('REC')) return 'REC';
                                  if (issuerName.includes('SBI')) return 'SBI';
                                  if (issuerName.includes('ICICI')) return 'ICICI';
                                  if (issuerName.includes('AXIS')) return 'AXIS';
                                  if (issuerName.includes('LIFE')) return 'LIFE';
                                  
                                  // For other companies, take first 2-4 characters
                                  const words = issuerName.split(' ');
                                  if (words.length >= 2) {
                                    return words.slice(0, 2).map(word => word.charAt(0)).join('');
                                  }
                                  return issuerName.substring(0, 3).toUpperCase();
                                })()}
                              </div>
                            </div>
                            <div className="issuer-text-info">
                              <div className="issuer-company-name">
                                {issuer.issuer || 'AXIS MAX LIFE INSURANCE LIMITED'}
                              </div>
                              <div className="issuer-category">
                                {issuer.sector || 'Life Insurance'}
                              </div>
                            </div>
                          </div>
                          <div className="issuer-right-section">
                            <div
                              className="arrow-circle"
                              style={{ cursor: 'pointer' }}
                            >
                              <ArrowUpOutlined style={{ transform: 'rotate(45deg)' }} />
                            </div>
                          </div>
                        </div>

                        {/* Bottom Section: Tags */}
                        <div className="issuer-bottom-section">
                          <div className="issuer-tags">
                            <Tag className="issuer-tag">
                              {issuer.typeOfIssuerBasedOnOwnership || 'NON PSU'}
                            </Tag>
                            <Tag className="issuer-tag">
                              {(() => {
                                // Use classification object if available, otherwise fallback to industry or p_industry
                                const industry = issuer.classification?.industry || issuer.p_industry || issuer.industry || 'Life Insurance';
                                return industry.length > 30 ? (
                                  <Tooltip title={industry}>
                                    {industry.substring(0, 30)}...
                                  </Tooltip>
                                ) : (
                                  industry
                                );
                              })()}
                            </Tag>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>
                );
              })
            )}
          </Row>
        </div>

        {/* Pagination */}
        {totalItems > pageSize && (
          <div className="pagination-container">
            <Pagination
              current={currentPage}
              total={totalItems}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger
              onShowSizeChange={handlePageChange}
              showTotal={(total) => `Total ${total} items`}
            />
          </div>
        )}

        {/* Empty State */}
        {issuersData.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <BankOutlined />
            </div>
            <Title level={3}>No issuers found</Title>
            <Paragraph>
              Try adjusting your search terms or browse all issuers
            </Paragraph>
            <Button
              type="primary"
              onClick={() => setSearchTerm('')}
              className="clear-search-btn"
            >
              Clear Search
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return skipLayout ? content : <LandingLayout>{content}</LandingLayout>;
};

export default Issuers;



